import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import ValidatedInput from "@/components/ValidatedInput";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Database, PaperPlaneTilt, ArrowClockwise, CheckCircle, XCircle, Warning,
} from "@phosphor-icons/react";

const DOC_TYPES = [
  { value: "cedula", label: "Cédula" },
  { value: "rnc", label: "RNC" },
  { value: "pasaporte", label: "Pasaporte" },
];

export default function CmsIntegrationPanel() {
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [health, setHealth] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testInput, setTestInput] = useState({ document_type: "cedula", document: "" });
  const [testDocValid, setTestDocValid] = useState(false);

  const [forbidden, setForbidden] = useState(false);

  const load = async () => {
    try {
      const [s, l, h] = await Promise.all([
        api.get("/integrations/cms/settings"),
        api.get("/integrations/cms/logs?limit=20"),
        api.get("/integrations/cms/health"),
      ]);
      setSettings(s.data);
      setLogs(l.data || []);
      setHealth(h.data);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401 || code === 403) {
        setForbidden(true);
      } else {
        toast.error("Error cargando configuración CMS");
      }
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/integrations/cms/settings", settings);
      setSettings(data);
      toast.success("Configuración CMS guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    if (!testInput.document.trim()) return toast.error("Ingresa un documento de prueba");
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/integrations/cms/test", testInput);
      setTestResult(data);
      if (data.ok) {
        toast.success(`OK: ${data.chassis.length} chasis`);
      } else {
        toast.error(data.error || "Error del CMS");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setTesting(false);
    }
  };

  if (forbidden) {
    return (
      <div className="border border-border rounded-lg p-6 bg-card">
        <div className="text-sm text-muted-foreground" data-testid="cms-forbidden">
          Solo administradores y supervisores pueden configurar la integración CMS.
        </div>
      </div>
    );
  }
  if (!settings) {
    return (
      <div className="border border-border rounded-lg p-6 bg-card">
        <div className="text-sm text-muted-foreground">Cargando configuración CMS…</div>
      </div>
    );
  }

  const patch = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  return (
    <div className="border border-border rounded-lg bg-card" data-testid="cms-integration-panel">
      <div className="p-5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-primary/15 text-primary flex items-center justify-center">
            <Database size={20} weight="fill" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-lg">CMS Custom · Consulta de Chasis</h3>
            <p className="text-xs text-muted-foreground">
              El SDM Concierge usa esta integración para ubicar los chasis de un cliente por su cédula/RNC/pasaporte.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={settings.enabled}
            onCheckedChange={(v) => patch("enabled", v)}
            data-testid="cms-enabled-switch"
          />
          <span className="text-xs uppercase tracking-wider">
            {settings.enabled ? "Activo" : "Deshabilitado"}
          </span>
        </div>
      </div>

      {/* Health status banner — surfaces CMS outages proactively */}
      {health && health.status !== "unknown" && (
        <div
          className={`mx-5 mt-4 rounded-md p-3 text-xs border flex items-start gap-2 ${
            health.status === "up" ? "bg-emerald-500/10 border-emerald-500/30" :
            health.status === "degraded" ? "bg-amber-500/10 border-amber-500/40" :
            "bg-rose-500/10 border-rose-500/40"
          }`}
          data-testid="cms-health-banner"
        >
          {health.status === "up" ? (
            <CheckCircle size={16} weight="fill" className="text-emerald-500 mt-0.5 flex-shrink-0" />
          ) : (
            <Warning size={16} weight="fill" className={health.status === "degraded" ? "text-amber-500 mt-0.5 flex-shrink-0" : "text-rose-500 mt-0.5 flex-shrink-0"} />
          )}
          <div className="flex-1">
            <div className={`font-semibold mb-0.5 ${
              health.status === "up" ? "text-emerald-500" :
              health.status === "degraded" ? "text-amber-600" : "text-rose-500"
            }`}>
              {health.status === "up" && `CMS operativo · ${health.recent_calls} llamadas recientes, 0 fallas`}
              {health.status === "degraded" && `CMS degradado · ${Math.round(health.failure_rate * 100)}% de fallas`}
              {health.status === "down" && `CMS caído · ${health.failures}/${health.recent_calls} llamadas fallando (HTTP ${health.last_http || "?"})`}
            </div>
            {health.status !== "up" && (
              <div className="text-muted-foreground font-mono text-[11px] break-all">
                Último error: {health.last_error || "(sin detalle)"}
                {health.last_call_at && ` · ${new Date(health.last_call_at).toLocaleString("es-DO")}`}
              </div>
            )}
            {health.status === "down" && (
              <div className="mt-1.5 pt-1.5 border-t border-rose-500/20 text-[11px] text-foreground">
                <strong>Acción:</strong> notifica al equipo de TI. El servidor Azure está retornando HTTP 500 vacío. El bot está avisando al cliente y ofreciendo escalación a asesor humano automáticamente.
              </div>
            )}
          </div>
        </div>
      )}
      <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings */}
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Base URL</Label>
            <Input
              value={settings.base_url}
              onChange={(e) => patch("base_url", e.target.value)}
              data-testid="cms-base-url"
              className="font-mono text-xs"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Path (unidades cliente)</Label>
            <Input
              value={settings.unidades_path}
              onChange={(e) => patch("unidades_path", e.target.value)}
              data-testid="cms-path"
              className="font-mono text-[11px]"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Tokens: <code>{"{tipoDoc}"}</code>, <code>{"{documento}"}</code>
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Cédula</Label>
              <Input value={settings.tipo_doc_cedula} onChange={(e) => patch("tipo_doc_cedula", e.target.value)} data-testid="cms-tipo-cedula" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">RNC</Label>
              <Input value={settings.tipo_doc_rnc} onChange={(e) => patch("tipo_doc_rnc", e.target.value)} data-testid="cms-tipo-rnc" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Pasaporte</Label>
              <Input value={settings.tipo_doc_pasaporte || "P"} onChange={(e) => patch("tipo_doc_pasaporte", e.target.value)} data-testid="cms-tipo-pasaporte" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Umbral escalación RNC</Label>
              <Input
                type="number"
                value={settings.rnc_escalation_threshold}
                onChange={(e) => patch("rnc_escalation_threshold", parseInt(e.target.value || "3", 10))}
                data-testid="cms-rnc-threshold"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Escala a agente humano si RNC tiene &gt; N chasis
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Umbral cédula (0=nunca)</Label>
              <Input
                type="number"
                value={settings.cedula_escalation_threshold}
                onChange={(e) => patch("cedula_escalation_threshold", parseInt(e.target.value || "0", 10))}
                data-testid="cms-cedula-threshold"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">API key header (opc.)</Label>
              <Input
                value={settings.api_key_header || ""}
                onChange={(e) => patch("api_key_header", e.target.value)}
                placeholder="X-Api-Key"
                data-testid="cms-key-header"
                className="font-mono text-xs"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">API key value</Label>
              <Input
                value={settings.api_key_value || ""}
                onChange={(e) => patch("api_key_value", e.target.value)}
                placeholder="******"
                type="password"
                data-testid="cms-key-value"
                className="font-mono text-xs"
              />
            </div>
          </div>
          <Button onClick={save} disabled={saving} data-testid="cms-save">
            {saving ? "Guardando…" : "Guardar configuración"}
          </Button>
        </div>

        {/* Test Tool */}
        <div className="space-y-3 border border-dashed border-border rounded-md p-4 bg-secondary/20">
          <div>
            <h4 className="font-semibold text-sm mb-1 flex items-center gap-2">
              <PaperPlaneTilt size={14} className="text-primary" />
              Probar conexión
            </h4>
            <p className="text-[11px] text-muted-foreground">
              Simula una consulta como si viniera del bot. Verás la respuesta y quedará registrada abajo.
            </p>
          </div>
          <div className="flex gap-2 items-start">
            <Select value={testInput.document_type} onValueChange={(v) => setTestInput({ ...testInput, document_type: v })}>
              <SelectTrigger className="w-32 shrink-0" data-testid="cms-test-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((d) => (<SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>))}
              </SelectContent>
            </Select>
            <div className="flex-1">
              <ValidatedInput
                field={testInput.document_type}
                value={testInput.document}
                onChange={(v) => setTestInput({ ...testInput, document: v })}
                onValidChange={(ok) => setTestDocValid(ok)}
                testId="cms-test-doc"
                required={false}
                placeholder={testInput.document_type === "cedula" ? "40212345678" : testInput.document_type === "rnc" ? "131123456" : "AB123456"}
              />
            </div>
            <Button onClick={runTest} disabled={testing || !testDocValid} data-testid="cms-test-run">
              {testing ? "…" : "Probar"}
            </Button>
          </div>

          {testResult && (
            <div className="border border-border rounded-md p-3 bg-background text-xs" data-testid="cms-test-result">
              <div className="flex items-center gap-2 mb-2">
                {testResult.ok ? (
                  <><CheckCircle size={14} weight="fill" className="text-emerald-500" /><span className="font-semibold text-emerald-500">OK</span></>
                ) : (
                  <><XCircle size={14} weight="fill" className="text-rose-500" /><span className="font-semibold text-rose-500">Error</span></>
                )}
                {testResult.escalate && (
                  <Badge className="bg-amber-500 text-white gap-1"><Warning size={11} /> escala</Badge>
                )}
                <span className="text-muted-foreground">
                  {testResult.chassis?.length ?? 0} chasis · {testResult.document_type}
                </span>
              </div>
              {testResult.error && <div className="text-rose-500 mb-2 font-mono text-[10px]">{testResult.error}</div>}
              {testResult.reason && <div className="text-amber-600 mb-2">{testResult.reason}</div>}
              {testResult.chassis?.slice(0, 5).map((c, i) => (
                <div key={i} className="border-t border-border pt-2 mt-2 first:border-t-0 first:pt-0 first:mt-0">
                  <div className="font-medium">{c.brand} {c.model} · {c.year}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">VIN: {c.vin}</div>
                  {c.body_type && <div className="text-[10px] text-muted-foreground">{c.body_type} · {c.version}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Logs */}
      <div className="p-5 border-t border-border">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-sm">Últimas 20 consultas al CMS</h4>
          <Button size="sm" variant="ghost" onClick={load}>
            <ArrowClockwise size={12} className="mr-1" /> Refrescar
          </Button>
        </div>
        {logs.length === 0 && (
          <div className="text-xs text-muted-foreground text-center py-6">Sin llamadas registradas todavía.</div>
        )}
        <div className="space-y-1.5 max-h-64 overflow-auto">
          {logs.map((l) => (
            <div key={l.id} className="flex items-center gap-3 text-xs border border-border rounded-md p-2 bg-background/50" data-testid={`cms-log-${l.id}`}>
              {l.success ? (
                <CheckCircle size={14} weight="fill" className="text-emerald-500 shrink-0" />
              ) : (
                <XCircle size={14} weight="fill" className="text-rose-500 shrink-0" />
              )}
              <span className="font-mono uppercase text-[10px] w-14 shrink-0">{l.document_type}</span>
              <span className="font-mono flex-1 truncate">{l.document}</span>
              <span className="text-muted-foreground shrink-0">HTTP {l.http_status ?? "—"}</span>
              <span className="text-muted-foreground shrink-0">{l.latency_ms ?? "—"}ms</span>
              <span className="text-muted-foreground shrink-0">{l.chassis_count} chasis</span>
              <span className="text-[10px] text-muted-foreground shrink-0">
                {new Date(l.created_at).toLocaleString("es-DO")}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
